import Cookies from "js-cookie";
import moment from "moment-timezone";
import {
  bizDomains,
  conversionRates,
  excelBaseDate,
  taxTypes,
} from "utils/constants";
import {
  OptionSetProps,
  OptionSetDetails,
  SideBarMenuResponse,
} from "utils/types";
import { BusinessInfo, KeyValue } from "utils/types/Commons";
import { Reports, ReportResult } from "utils/types/Reports";
import { DateInput, FormDataPayload } from "./index.types";

export const convertMinutesInToHours = (mins: number) => {
  if (mins === 0) return;

  const numberOfHours = Math.floor(mins) / 60;
  const days = Math.floor(numberOfHours / 24);
  const remainder = numberOfHours % 24;
  const hours = Math.floor(remainder);
  const minutes = Math.floor(60 * (remainder - hours));
  // if less than 1 Day/ 24 hours then only show Hours and Minutes
  if (days < 1) {
    // if less than Hour return only Minutes
    if (hours < 1) {
      return `${minutes} min(s)`;
    }
    return `${hours} hr(s) ${minutes} min(s)`;
  } else {
    // show only days and minutes if hour is less than 1
    if (hours < 1) {
      return `${days} day(s) ${minutes} min(s)`;
    }
    // return All days
    return `${days} day(s) ${hours} hr(s) and ${minutes} min(s)`;
  }
};

export const replaceItemAtIndexInArray = (
  index: number,
  item: any,
  itemArray: any
) => {
  const newData = [
    ...itemArray.slice(0, index),
    item,
    ...itemArray.slice(index + 1),
  ];
  return newData;
};

// Throw error message of min and  max required
export const getHeaderString = (
  minMaxOptionalObject: OptionSetProps
): string => {
  let strToReturn = "";

  if (minMaxOptionalObject.required) {
    strToReturn += "Required";
  } else {
    strToReturn += "Optional";
  }
  if (minMaxOptionalObject.min === "0") {
    strToReturn += " Max = " + minMaxOptionalObject.max;
  } else {
    strToReturn +=
      " Min = " +
      minMaxOptionalObject.min +
      ", Max = " +
      minMaxOptionalObject.max;
  }
  return strToReturn;
};

// get Min Max for Option set
export const getMinMaxAndRequired = (
  optionHead: OptionSetProps
): OptionSetDetails => {
  const minQty: string | number = optionHead?.min_quantity || 0;
  const qty: string | number = optionHead?.quantity || 0;

  let optionSetDetails: OptionSetDetails = {
    min: "",
    max: "",
    required: false,
  };

  // optional case
  if (minQty === "0") {
    if (qty === "0" || qty === "") {
      optionSetDetails = {
        min: 0,
        max: optionHead?.items?.length || 0,
        required: false,
      };
    } else {
      optionSetDetails = {
        min: 0,
        max: qty,
        required: false,
      };
    }
  }
  // required case
  else {
    if (minQty === "") {
      if (qty === "0") {
        optionSetDetails = {
          min: optionHead?.items?.length || 0,
          max: optionHead?.items?.length || 0,
          required: true,
        };
      } else {
        optionSetDetails = {
          min: qty,
          max: qty,
          required: true,
        };
      }
    } else {
      if (qty === "0" || qty === "") {
        optionSetDetails = {
          min: minQty,
          max: optionHead?.items?.length || 0,
          required: true,
        };
      } else {
        optionSetDetails = {
          min: minQty,
          max: qty,
          required: true,
        };
      }
    }
  }

  return optionSetDetails;
};

export const getLocalStorage = (): Partial<BusinessInfo> => {
  const businessInfo: string | null = localStorage.getItem("businessInfo");

  if (businessInfo) {
    const {
      eatout_id,
      user_id,
      branch_id,
      eatout_name,
      eatout_username,
      branch_name,
    }: BusinessInfo = JSON.parse(businessInfo);

    return {
      eatout_id,
      user_id,
      branch_id,
      eatout_name,
      eatout_username,
      branch_name,
    };
  }

  return {};
};

// clear local storage on logout
export const clearStorage = () => {
  localStorage.removeItem("allBusinessesInfo");
  localStorage.removeItem("businessInfo");
  localStorage.removeItem("tdLogin");

  // remove main reducer from local storage
  localStorage.removeItem("ts-global");
};

export const authenticateUser = () => {
  return localStorage.getItem("tdLogin") ? true : false;
};

export const compareItem = (
  a: { label: string; value: string },
  b: { label: string; value: string }
) => {
  // a should come before b in the sorted order
  if (a.label.toLowerCase() < b.label.toLowerCase()) {
    return -1;
    // a should come after b in the sorted order
  } else if (a.label.toLowerCase() > b.label.toLowerCase()) {
    return 1;
    // and and b are the same
  } else {
    return 0;
  }
};

export const slugify = (text: string) => {
  return (
    text &&
    text
      .toString()
      .trim()
      .toLowerCase()
      // remove any special character with spaces
      .replace(/[\W_]+/g, " ")
      // remove more than one spaces and dashes
      .replace(/\s+|-+/g, "-")
      // remove -dashes from start of string
      .replace(/^\-/i, " ")
  );
};

export const isValidSlug = (text: string) => {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(text);
};

export const capitalizeFLetter = (value: string | null) => {
  // handle null, undefined etc
  if (!value) return "";

  let splitStr = value.toLowerCase().split(" ");
  for (let i = 0; i < splitStr.length; i++) {
    splitStr[i] =
      splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  // Directly return the joined string
  return splitStr.join(" ");
};

export const formatSpecialStrings = (sentence: string) => {
  if (!sentence) return "";

  return sentence
    .split("_")
    .map((i: string) => i.charAt(0).toUpperCase() + i.slice(1))
    .join(" ");
};

const handleLeadingZero = (n: number) => (n < 10 ? "0" + n : n);

// Format time
export const formatDate = (date: any) => {
  return (
    date.getFullYear() +
    "-" +
    handleLeadingZero(date.getMonth() + 1) +
    "-" +
    handleLeadingZero(date.getDate()) +
    " " +
    handleLeadingZero(date.getHours()) +
    ":" +
    handleLeadingZero(date.getMinutes()) +
    ":00"
  );
};

const getFormatedTodayDate = () => {
  let date = new Date();
  let today = `${handleLeadingZero(date.getDate())}-${handleLeadingZero(
    date.getMonth() + 1
  )}-${date.getFullYear()}`;
  return today;
};

// Drag And drop For Product listing Reorder Function
export const reorder = (list: any, sequence: any) => {
  const result = Array.from(list);
  const [removed] = result.splice(sequence[0].source, 1);
  result.splice(sequence[0].destination, 0, removed);

  return result;
};

export const sortMenuItems = (
  shortValues: any,
  type: string,
  shortType: string
) => {
  if (shortType === "itemShort") {
    shortValues.map((Items: any) => {
      if (type === "0") {
        Items.menu_item = Items.menu_item.sort((a: any, b: any) => {
          if (a.name < b.name) return -1;
          if (b.name < a.name) return 1;
          return 0;
        });
      } else {
        Items.menu_item = Items.menu_item.sort((a: any, b: any) => {
          if (a.name > b.name) return -1;
          if (b.name > a.name) return 1;
          return 0;
        });
      }
    });
  }
};

// function to format date & time
export const validateDateTimeFormat = (data: string) => {
  return data && data !== "0000-00-00 00:00:00"
    ? moment(data).format(
        data?.includes("00:00:00") ? "MMM Do, YYYY" : "MMM Do, YYYY hh:mm a"
      )
    : "-";
};

export const getFormatTime = (date: DateInput): string => {
  if (!date) {
    return "";
  }

  // guess the local timezone
  const timezone = moment.tz.guess();

  let formattedDate;

  if (typeof date === "number") {
    /**
     * create a moment object for the Excel epoch start date in the local timezone
     * add the Excel serial date value as days to the epoch start date
     */
    const excelEpoch = moment.tz(excelBaseDate, timezone).add(date, "days");

    // format the resulting date
    formattedDate = excelEpoch.format("YYYY-MM-DD");
  } else if (typeof date === "string") {
    // convert date into moment object
    const momentDate: moment.Moment = moment(date);

    // verify the date and convert to string
    formattedDate = momentDate?.isValid()
      ? momentDate?.format("YYYY-MM-DD")
      : "";
  } else {
    // if date is neither number nor string, return an empty string
    return "";
  }

  return formattedDate;
};

//if inputString is not empty and typeof inputString is string than function return 1 else return 0
export const isString = (inputString: string) => {
  return inputString !== "" && typeof inputString === "string" ? 1 : 0;
};

export const priceValidation = (price: string) => {
  return !/^[0-9.]*$/.test(price) || typeof price === "string" || price === "";
};

// returns the display source name
export const setDisplaySource = (sourceNumber: string) => {
  switch (sourceNumber) {
    case "0": {
      return "All";
    }
    case "1": {
      return "None";
    }
    case "2": {
      return "Web";
    }
    case "3": {
      return "POS";
    }
    default: {
      return "";
    }
  }
};

// Images Compress function
export const hasAlpha = (
  context: CanvasRenderingContext2D | null,
  canvas: HTMLCanvasElement
) => {
  let data =
      context && context.getImageData(0, 0, canvas.width, canvas.height).data,
    hasAlphaPixels = false;

  if (data) {
    const n = data.length;
    for (let i = 3; i < n; i += 4) {
      if (data[i] < 255) {
        hasAlphaPixels = true;
        break;
      }
    }
  }
  return hasAlphaPixels;
};

export const sharpen = (
  ctx: {
    createImageData: (arg0: any, arg1: any) => any;
    getImageData: (
      arg0: number,
      arg1: number,
      arg2: any,
      arg3: any
    ) => { (): any; new (): any; data: any };
    putImageData: (arg0: number, arg1: number, arg2: number) => void;
  },
  w: number,
  h: number,
  mix: number
) => {
  let x,
    sx,
    sy,
    r,
    g,
    b,
    a,
    dstOff,
    srcOff,
    wt,
    cx,
    cy,
    scy,
    scx,
    weights = [0, -1, 0, -1, 5, -1, 0, -1, 0],
    katet = Math.round(Math.sqrt(weights.length)),
    half = (katet * 0.5) | 0,
    dstData = ctx.createImageData(w, h),
    dstBuff = dstData.data,
    srcBuff = ctx.getImageData(0, 0, w, h).data,
    y = h;

  while (y--) {
    x = w;
    while (x--) {
      sy = y;
      sx = x;
      dstOff = (y * w + x) * 4;
      r = 0;
      g = 0;
      b = 0;
      a = 0;

      for (cy = 0; cy < katet; cy++) {
        for (cx = 0; cx < katet; cx++) {
          scy = sy + cy - half;
          scx = sx + cx - half;

          if (scy >= 0 && scy < h && scx >= 0 && scx < w) {
            srcOff = (scy * w + scx) * 4;
            wt = weights[cy * katet + cx];

            r += srcBuff[srcOff] * wt;
            g += srcBuff[srcOff + 1] * wt;
            b += srcBuff[srcOff + 2] * wt;
            a += srcBuff[srcOff + 3] * wt;
          }
        }
      }

      dstBuff[dstOff] = r * mix + srcBuff[dstOff] * (1 - mix);
      dstBuff[dstOff + 1] = g * mix + srcBuff[dstOff + 1] * (1 - mix);
      dstBuff[dstOff + 2] = b * mix + srcBuff[dstOff + 2] * (1 - mix);
      dstBuff[dstOff + 3] = srcBuff[dstOff + 3];
    }
  }

  ctx.putImageData(dstData, 0, 0);
};

export const dataURLtoFile = (dataurl: string, filename: string) => {
  const arr = dataurl.split(",");
  if (arr[0].length > 0) {
    let mime = null;
    const match = arr[0].match(/:(.*?);/);
    if (match) {
      mime = match[1];
    }
    const bstr = atob(arr[1]);

    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime as string });
  }
};

// remove tags from String
export const removeTags = (text: string) => {
  return text && text.toString().replace(/(<([^>]+)>)/gi, "");
};

export const makeUsername = (text: string) => {
  return (
    text &&
    text
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+|-+/g, "")
      .replace(/^\-/i, " ")
  );
};

// Receive an array of string and return formatted string
export const formatString = (arrayOfString: string[]) => {
  const count = arrayOfString.length > 2 ? ` +${arrayOfString.length - 2}` : "";
  const lastComma = arrayOfString.length !== 2 ? ", " : "";

  return arrayOfString.length > 1
    ? `${arrayOfString[0] + ", " + arrayOfString[1] + lastComma}` + count
    : arrayOfString[0];
};

export const getBranch = () => {
  const { access_management } = JSON.parse(
    localStorage.getItem("businessInfo")!
  );
  if (access_management.branch.length > 0) {
    return access_management.branch[0];
  }
  return 0;
};
// make data for line and bar charts like sales and orders chart
export const makeDataForGraph = (
  result: ReportResult[],
  formatDate: boolean = true,
  tableData?: boolean
) => {
  const xAxisLabels: string[] = [],
    orders: number[] = [],
    sales: number[] = [],
    keyValues: KeyValue[] = [];
  let grossSales = 0,
    netSales = 0,
    totalTax = 0,
    deliveryCharges = 0,
    serviceCharges = 0;

  result.map((dataObj: ReportResult) => {
    if (tableData) {
      keyValues.push({
        key: dataObj.key,
        value: dataObj["doc_count"].value,
      });
    } else {
      if (formatDate) {
        xAxisLabels.push(
          moment(dataObj.key, "YYYY-MM-DD").format("DD MMM").toString()
        );
      } else {
        xAxisLabels.push(dataObj.key);
      }

      orders.push(dataObj["doc_count"].value);
      sales.push(Number(dataObj.total.value.toFixed(2)));

      grossSales = grossSales + dataObj.total.value;
      netSales += dataObj.sub_total.value;

      totalTax += dataObj.tax_total.value;
      if (dataObj?.delivery_charges) {
        deliveryCharges += dataObj?.delivery_charges.value;
      } else if (dataObj?.service_charges) {
        serviceCharges += dataObj?.service_charges.value;
      }
    }
  });

  return {
    xAxisLabels,
    orders,
    sales,
    keyValues,
    grossSales,
    netSales,
    totalTax,
    deliveryCharges,
    serviceCharges,
  };
};

export const formatNumber = (num: string) => {
  if (!num) return "";
  return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
};

export const convertStringToKey = (key: string) => {
  return key
    .replace(/\b\w/g, (match) => match.toLowerCase())
    .replace(/\s+/g, "_");
};

export const validateMenuJSONResponse = (data: SideBarMenuResponse) => {
  if (
    data?.hasOwnProperty("access") &&
    data?.access?.hasOwnProperty("modules") &&
    data?.access?.modules?.length
  ) {
    return true;
  } else {
    return false;
  }
};

// convert digital time into hr & min
export const formatTime = (inputTime: string) => {
  if (inputTime && inputTime.includes(":")) {
    // map(Number) transforms the array of strings into an array of numbers
    const [hours, minutes] = inputTime.split(":").map(Number);

    const formattedHours = `${hours} hr`;
    const formattedMinutes = `${minutes} min`;

    return `${formattedHours} ${formattedMinutes}`.trim();
  } else {
    return inputTime.trim();
  }
};

// format dates & time in meridian format
export const meridianDateFormat = (dateString: string | undefined) => {
  return {
    formattedTime: dateString ? moment(dateString).format("hh:mm A") : "- -",
    formattedDate: dateString
      ? moment(dateString).format("DD MMM YYYY")
      : "- -",
  };
};

export const calculateElapsedTime = (inputDate: string) => {
  const inputTime = new Date(inputDate).getTime();
  const currentTime = new Date().getTime();
  const elapsedTimeInMilliseconds = currentTime - inputTime;
  // Calculate individual units
  const seconds = Math.floor(elapsedTimeInMilliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days < 1) {
    // if less than Hour return only Minutes
    if (hours < 1) {
      return `${minutes} min(s) ago`;
    }
    return `${hours} hr(s) ${minutes} min(s) ago`;
  } else {
    // show only days and minutes if hour is less than 1
    if (hours < 1) {
      return `${days} day(s) ${minutes} min(s)  ago`;
    }
    // return All days
    return `${days} day(s) ${hours} hr(s)  ago`;
  }
};

export const truncateStringWithEllipsis = (str: string, maxLength: number) => {
  return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
};

export const formatDateTimeString = (dateTimeString: string) => {
  if (!dateTimeString) return;

  if (dateTimeString == "0000-00-00 00:00:00") {
    return "-";
  } else {
    const fields = dateTimeString.split(" ");

    if (fields[1] === "00:00:00") {
      return moment(dateTimeString).format("ll");
    } else {
      return moment(dateTimeString).format("lll");
    }
  }
};

export const capitalizeWords = (inputString: string) => {
  return inputString.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const queryStringToObject = (queryString: string) => {
  const obj: any = {};
  const keyValuePairs = queryString.split("&");

  for (const pair of keyValuePairs) {
    const [key, value] = pair.split("=");
    if (key && value !== undefined) {
      obj[key] = decodeURIComponent(value);
    }
  }

  return obj;
};

// function to return count of repeating data
export const generateRepeatingDataCount = (data: string[]) => {
  const counterResult: Record<string, number> = {};

  data?.forEach((entity) => {
    counterResult[entity] = (counterResult[entity] || 0) + 1;
  });

  return counterResult;
};

// function to return filtered taxType from constants
export const filterTaxType = (data: string) => {
  return taxTypes?.filter((taxType) => taxType.label === data);
};

export const escapeSingleDoubleQuotes = (text: string) => {
  // Replace both single and double quotes with their escaped versions
  return text.replace(/['"]/g, (match) => {
    return match === "'" ? "\\'" : '\\"';
  });
};

export function convertTimeToMinutes(timeString: string) {
  // Regular expression to match days, hours, and minutes
  const regex = /(\d+d)? ?(\d+h)? ?(\d+m)?/;

  // Extract days, hours, and minutes from the input string
  const [, days, hours, minutes]: any = timeString.match(regex);

  // Parse and calculate total minutes
  const totalMinutes =
    (parseInt(days) || 0) * 24 * 60 +
    (parseInt(hours) || 0) * 60 +
    (parseInt(minutes) || 0);

  return totalMinutes.toString();
}

export function getTimeZoneLabelValue(timeZoneData: any) {
  // convert timestamp in required format of time zone
  const dstOffsetSeconds = timeZoneData?.dstOffset ?? 0;
  const rawOffsetSeconds = timeZoneData?.rawOffset ?? 0;

  const timeZoneOffset = dstOffsetSeconds + rawOffsetSeconds;
  const offsetHours = timeZoneOffset / 3600;
  const sign = offsetHours >= 0 ? "+" : "-";
  const hours = Math.abs(Math.floor(offsetHours));
  const timeZoneOffsetString = `${sign}${hours.toString().padStart(2, "0")}:00`;

  const label = timeZoneData.timeZoneId;
  return { label, value: timeZoneOffsetString };
}

export const calculatePercentageChange = (
  previous: number,
  current: number
) => {
  if (previous === 0 || current === 0) return 0;

  return ((current - previous) / previous) * 100;
};

export const formatLabel = (date: string) =>
  moment(date, "YYYY-MM-DD").format("DD MMM").toString();

export const getCookie = (key: string) => {
  const data = Cookies.get(key);

  if (data) {
    return data;
  }
  return "";
};

export const getLocalStorageKey = (key: string) => {
  const getKey = localStorage.getItem(key);
  if (getKey) {
    return JSON.parse(getKey);
  }
  return [];
};

export const isValidValue = (value: any): boolean => {
  return (
    value === 0 ||
    value === "" ||
    typeof value === "number" ||
    (typeof value === "string" && !isNaN(Number(value)))
  );
};

export function arraysAreEqual(arr1: string[], arr2: string[]) {
  // Check if the arrays have the same length
  if (arr1.length !== arr2.length) {
    return false;
  }

  // Check if each element in the arrays is the same
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  // If all elements are the same, return true
  return true;
}

export const getFormDataPayloadAsJson = (
  formData: FormData
): FormDataPayload => {
  const payload: FormDataPayload = {};

  formData.forEach((value, key) => {
    payload[key] = value.toString(); // Convert values to string
  });

  return payload;
};

export function addValueToArray(array: string[]) {
  if (array?.includes("Net Sale Items")) return array;

  const indexDocCount = array?.indexOf("doc_count");
  array?.splice(indexDocCount + 1, 0, "Net Sale Items");
  return array;
}

// convert weight to grams
export const convertToGrams = (value: number, unit: string) => {
  return value * (conversionRates[unit] || 1);
};

// convert weight from grams to provided unit
export const convertFromGrams = (value: number, unit: string) => {
  return value / (conversionRates[unit] || 1);
};

export const getBizDomain = () => {
  return window.location.href.includes(bizDomains?.ORDRZ);
};

// validate order subscription JSON and return subscription type
export const getSubscription = (subscription: string) => {
  if (subscription) {
    const subscriptionDetails = JSON.parse(subscription);

    return `${capitalizeFLetter(
      subscriptionDetails?.subscription_type
    )} subscription #${subscriptionDetails?.subscription_id}`;
  }

  return "";
};

// update the meta tags for tossdown.com or ordrz.com
export const updateMetaTags = () => {
  let faviconUrl = "";
  let title = "";
  let siteName = "";
  let ogUrl = "";
  let ogDescription = "";
  let ogImage = "";
  let publisher = "";
  let author = "";
  let faviconLink = document.querySelector(
    'link[rel="icon"]'
  ) as HTMLLinkElement;

  const url = window.location.href;
  const isOrdrz = url.includes("ordrz");

  if (isOrdrz) {
    faviconUrl =
      "https://static.tossdown.com/logos/4a27f5c1-99d9-4950-89f5-f82b249f3e17.svg";

    title = "Business | ORDRZ";

    // TODO: required later
    // siteName = "ordrz.com";

    // TODO: required later
    // ogUrl = "https://ordrz.com/";

    // TODO: required later
    // ogDescription =
    //   "Business | ORDRZ - Admin dashboard panel for making online selling easy for you.";

    // TODO: required later
    // ogImage =
    //   "https://static.tossdown.com/images/f859d36a-6a5e-43c0-a4fc-c6ab97eebfc4.webp";

    publisher = "https://www.facebook.com/ordrz";

    author = "ORDRZ";
  } else {
    faviconUrl =
      "https://static.tossdown.com/logos/c3c2c243-1fa9-447d-ba4b-5ad5c2e38f2e.svg";

    title = "Business | tossdown";

    // TODO: required later
    // siteName = "tossdown.com";

    // TODO: required later
    // ogUrl = "https://tossdown.com/";

    // TODO: required later
    // ogDescription =
    //   "Business | tossdown - Admin dashboard panel for making online selling easy for you.";

    // TODO: required later
    // ogImage =
    //   "https://images-beta.tossdown.com/images/8881ee74-3813-4756-95de-e12f1c6987e0.webp";

    publisher = "https://www.facebook.com/tossdownpk";

    author = "tossdown";
  }

  // update the title
  document.title = title;

  // update the favicon link element
  if (faviconLink) {
    faviconLink.href = faviconUrl;
  }

  // TODO: required later
  // // update Open Graph site name
  // const metaSiteName = document.querySelector('meta[property="og:site_name"]');
  // if (metaSiteName) {
  //   metaSiteName.setAttribute("content", siteName);
  // }

  // TODO: required later
  // // update Open Graph URL
  // const metaOgUrl = document.querySelector('meta[property="og:url"]');
  // if (metaOgUrl) {
  //   metaOgUrl.setAttribute("content", ogUrl);
  // }

  // TODO: required later
  // // update Open Graph description
  // const metaOgDescription = document.querySelector(
  //   'meta[property="og:description"]'
  // );
  // if (metaOgDescription) {
  //   metaOgDescription.setAttribute("content", ogDescription);
  // }

  // TODO: required later
  // // update Open Graph image
  // const metaOgImage = document.querySelector('meta[property="og:image"]');
  // if (metaOgImage) {
  //   metaOgImage.setAttribute("content", ogImage);
  // }

  // update article publisher
  const metaPublisher = document.querySelector(
    'meta[property="article:publisher"]'
  );
  if (metaPublisher) {
    metaPublisher.setAttribute("content", publisher);
  }

  // update author meta tag
  const metaAuthor = document.querySelector('meta[name="author"]');
  if (metaAuthor) {
    metaAuthor.setAttribute("content", author);
  }
};

// function to return powered bu tossdown or ordrz
export const getPoweredByText = () => {
  return getBizDomain() ? "Powered by ordrz.com" : "Powered by tossdown.com";
};

// function to return the website url on sign-up
export const generateSiteURL = () => {
  const siteUrl = window.location.href;
  if (siteUrl.includes("staging")) {
    return siteUrl.includes("ordrz")
      ? bizDomains?.STAGING_DOMAIN?.replace("tossdown.site", "ordrz.com")
      : bizDomains?.STAGING_DOMAIN;
  } else {
    return siteUrl.includes("ordrz")
      ? bizDomains?.LIVE_DOMAIN?.replace("tossdown.site", "ordrz.com")
      : bizDomains?.LIVE_DOMAIN;
  }
};
